import { lazy, Suspense, useEffect } from "react";
import { CssBaseline, Skeleton } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

import { Auth0Provider } from "@auth0/auth0-react";
import { PostHogPageViewTracker } from "utils/posthogRouterIntegration";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Route,
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
  Outlet,
  Navigate,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";

import "react-toastify/dist/ReactToastify.css";
import "./App.css";
import "./toast.css";
import "@fontsource/poppins";
import "dayjs/locale/en-gb";

import { useTheme } from "contexts/ThemeContext";

import CONFIG from "utils/config";
import NewProject from "containers/NewProject";
import PublicDocs from "containers/PublicDocs/PublicDocs";
import { WorkspacesProvider } from "contexts/WorkspacesContext";
import { initializeO11y } from "utils/o11y/faro";
import {
  withFaroRouterInstrumentation,
} from "@grafana/faro-react";
import StaffUserManagement from "containers/StaffUserManagement";
import AppSettings from "containers/Staff/AppSettings";
import ErrorElement from "./ErrorElement";
import Floors from "containers/Floors/Floors";
import BIMs from "containers/BIMs/BIMs";
import ViewerWrapper from "components/ViewerWrapper";
import { LoadingProvider } from "contexts/LoadingContext";
import RedirectToViewer from "containers/RedirectToViewer";
import { ApsSuccess } from "containers/ApsSuccess";
import ProjectAnalytics from "containers/ProjectAnalytics";
import { ApsProvider } from "contexts/ApsContext";
import ExperimentalPayments from "containers/Subscription";
import GaussianSplatViewer from "containers/GaussianSplatViewer/GSplatViewerPage";

const Providers = lazy(() => import("contexts"));
const AuthProvider = lazy(() => import("contexts/AuthContext"));
const NotFound = lazy(() => import("./NotFound"));

const PublicLayout = lazy(() => import("components/PublicLayout/PublicLayout"));
const AppLayout = lazy(() => import("components/AppLayout"));
const StaffLayout = lazy(() => import("components/StaffLayout"));

const CompareScans = lazy(() => import("containers/CompareScans"));
const ImageGallery = lazy(() => import("containers/ImageGallery"));
const Onboarding = lazy(() => import("containers/Onboarding"));
const Project = lazy(() => import("containers/Project"));
const ProjectSettings = lazy(() => import("containers/Project/ProjectSettings"));
const ScanDetails = lazy(() => import("containers/ScanDetails"));
const AdvancedSettingsScreen = lazy(
  () => import("containers/ScanDetails/components/AdvancedSettings")
);
const Staff = lazy(() => import("containers/Staff"));
const TransformProject = lazy(
  () => import("containers/Staff/TransformProject")
);
const TransformProjects = lazy(
  () => import("containers/Staff/TransformProjects")
);
const WorkspaceDetails = lazy(
  () => import("containers/Staff/WorkspaceDetails")
);
const Workspaces = lazy(() => import("containers/Staff/Workspaces"));
const TimeLine = lazy(() => import("containers/TimeLine"));
const Transactions = lazy(() => import("containers/Transactions"));
const Usage = lazy(() => import("containers/Usage"));
const UserSettings = lazy(() => import("containers/UserSettings"));
const VirtualTourViewer = lazy(
  () => import("containers/VirtualTourViewer/VirtualTourViewer")
);
const Projects = lazy(() => import("containers/Workspace"));
const WorkspaceSettings = lazy(() => import("containers/WorkspaceSettings"));
const DeveloperTools = lazy(() => import("containers/DeveloperTools"));
const GraphQLSandbox = lazy(() => import("containers/GraphQLSandbox"));
const AnnotationNote = lazy(() => import("containers/AnnotationNote"));
const FloorplanAlignment = lazy(() => import("containers/FloorplanAlignment"));
const CalibrateWithFloorPage = lazy(() => import("./pages/CalibrateWithFloorPage/CalibrateWithFloorPage"));
const ApsViewerPage = lazy(() => import("./pages/ApsViewerPage"));
const AllAnalyticsPage = lazy(() => import("./pages/AllAnalyticsPage"));
const GanttDemo = lazy(() => import("containers/GanttDemo"));

// const CommentsWithLexicalEditor = lazy(
//   () => import("containers/ExperimentalFeatures/CommentsWithLexicalEditor")
// );

// Create TanStack QueryClient
const queryClient = new QueryClient();

function App() {
  const theme = useTheme();

  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route path="/" errorElement={<ErrorElement />}>
        <Route path="something-went-wrong" element={<ErrorElement />} />

        {/* Public Routes */}
        <Route
          path="public"
          element={
            <WorkspacesProvider allowPublic={true}>
              <PublicLayout>
                <Suspense
                  fallback={
                    <Skeleton
                      height="100%"
                      sx={{
                        transform: "none",
                        WebkitTransform: "none",
                      }}
                    />
                  }
                >
                  <ApsProvider>
                  <Outlet />
                  </ApsProvider>
                </Suspense>
              </PublicLayout>
            </WorkspacesProvider>
          }
        >
          <Route path=":workspaceSlug/:projectSlug" element={<Project />} />
          <Route path=":workspaceSlug/:projectSlug/:scanId" element={<ScanDetails />} />
          <Route
            path=":workspaceSlug/:projectSlug/:scanId/virtual-tour/:outputId"
            element={<RedirectToViewer isPublic={true} outputType="vtour" />}
          />
          <Route
            path=":workspaceSlug/:projectSlug/:scanId/point-cloud/:outputId"
            element={<RedirectToViewer isPublic={true} outputType="ptcld" />}
          />
          <Route
            path=":workspaceSlug/:projectSlug/compare"
            element={<CompareScans />} // compare scans
          />
          <Route
            path=":workspaceSlug/:projectSlug/:scanId/viewer"
            element={
              <LoadingProvider>
                <ViewerWrapper />
              </LoadingProvider>
            }
          />
          <Route
            path=":workspaceSlug/:projectSlug/:scanId/viewer"
            element={
              <LoadingProvider>
                <ViewerWrapper />
              </LoadingProvider>
            }
          />
          {/* If none is found redirect user to sign in */}
          <Route path="*" element={<Navigate to="/" />} />
          <Route path="" element={<Navigate to="/" />} />
        </Route>

        

        {/* Authenticated Routes */}
        <Route
          path=""
          element={
            <AuthProvider>
              <Providers>
                <Outlet />
              </Providers>
            </AuthProvider>
          }
        >
          <Route
            path="/staff"
            element={
              <StaffLayout>
                <Suspense
                  fallback={
                    <Skeleton
                      height="100%"
                      sx={{
                        transform: "none",
                        WebkitTransform: "none",
                      }}
                    />
                  }
                >
                  <Outlet />
                </Suspense>
              </StaffLayout>
            }
          >
            <Route path="" element={<Staff />} />
            <Route path="onboarding" element={<Onboarding />} />
            <Route path="workspaces" element={<Workspaces />} />
            <Route path="user-management" element={<StaffUserManagement />} />
            <Route path="app-settings" element={<AppSettings />} />
            <Route
              path="workspaces/:workspaceSlug"
              element={<WorkspaceDetails />}
            />
            <Route
              path="workspaces/:workspaceSlug/projects"
              element={<TransformProjects />}
            />
            <Route
              path="workspaces/:workspaceSlug/projects/:projectId"
              element={<TransformProject />}
            />
          </Route>
          <Route path="/oauth/aps/callback" element={<ApsSuccess />} />
          <Route
            path="/"
            element={
              <AppLayout>
                <Suspense
                  fallback={
                    <Skeleton
                      height="100%"
                      sx={{
                        transform: "none",
                        WebkitTransform: "none",
                      }}
                    />
                  }
                >
                  <Outlet />
                </Suspense>
              </AppLayout>
            }
          >
            {/* manual / content routes */}
            <Route
              path="docs/help-center"
              element={<PublicDocs type="help-center" />}
            />
            <Route
              path="docs/changelog"
              element={<PublicDocs type="changelog" />}
            />
            <Route
              path="docs/public-api"
              element={<PublicDocs type="public-api" />}
            />

            <Route path="profile" element={<UserSettings />} />
            <Route path=":workspaceSlug/buy" element={<ExperimentalPayments />} />
            <Route path=":workspaceSlug/new-project" element={<NewProject />} />
            <Route
              path=":workspaceSlug"
              element={<Projects />} // List of projects
            />
            <Route
              path=":workspaceSlug/experimental/point-cloud-viewer"
              // element={<PointCloudViewer />}
            />

            <Route
              path=":workspaceSlug/experimental/gaussian-splat-viewer"
              element={<GaussianSplatViewer />}
            />
            
            <Route
              path=":workspaceSlug/:projectSlug"
              element={<Project />} // List of Scans
            />
            <Route
              path=":workspaceSlug/:projectSlug/settings"
              element={<ProjectSettings />}
            />
            <Route
              path=":workspaceSlug/:projectSlug/annotation-notes"
              element={<AnnotationNote />}
            />
            <Route
              path=":workspaceSlug/:projectSlug/:scanId"
              element={<ScanDetails />} // Scan with all it's info (Former Project)
            />
            <Route
              path=":workspaceSlug/:projectSlug/compare"
              element={<CompareScans />} // compare scans
            />

            <Route
              path=":workspaceSlug/:projectSlug/floors"
              element={<Floors />}
            />

            <Route
              path=":workspaceSlug/:projectSlug/bims"
              element={<BIMs />}
            />

            <Route
              path=":workspaceSlug/:projectSlug/floorplan-alignment"
              element={<FloorplanAlignment />}
            />

            <Route
              path=":workspaceSlug/:projectSlug/:scanId/virtual-tour/:outputId"
              element={<RedirectToViewer outputType="vtour" />}
            />

            <Route
              path=":workspaceSlug/:projectSlug/:scanId/point-cloud/:outputId"
              element={<RedirectToViewer outputType="ptcld" />}
            />

            <Route
              path=":workspaceSlug/:projectSlug/gantt"
              element={<GanttDemo />}
            />

            <Route
              path=":workspaceSlug/:projectSlug/:scanId/viewer"
              element={
                <LoadingProvider>
                  <ViewerWrapper />
                </LoadingProvider>
              }
            />
            <Route
              path=":workspaceSlug/:projectSlug/:scanId/advance-settings"
              element={<AdvancedSettingsScreen />} // Scan advanced settings
            />
            <Route
              path=":workspaceSlug/:projectSlug/:scanId/files"
              element={<ImageGallery />} // Files for the Scan (There's only one input now)
            />
            <Route
              path=":workspaceSlug/:projectSlug/:scanId/calibrate-with-floor"
              element={<CalibrateWithFloorPage />}
            />
            <Route
              path=":workspaceSlug/transactions"
              element={<Transactions />}
            />
            <Route
              path=":workspaceSlug/settings"
              element={<WorkspaceSettings />}
            />
            <Route
              path=":workspaceSlug/developer-tools"
              element={<DeveloperTools />}
            />
            <Route
              path=":workspaceSlug/graphql-sandbox"
              element={<GraphQLSandbox />}
            />
            <Route path=":workspaceSlug/timeline" element={<TimeLine />} />
            <Route path=":workspaceSlug/usage" element={<Usage />} />
            <Route path=":workspaceSlug/:projectSlug/analytics" element={<ProjectAnalytics />} />
            <Route path=":workspaceSlug/:projectSlug/all-analytics" element={<AllAnalyticsPage />} />
            <Route path=":workspaceSlug/aps-viewer" element={
              <LoadingProvider>
                <ApsViewerPage />
              </LoadingProvider>
            } />
          </Route>
          <Route path="/onboarding" element={<Onboarding />} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Route>
    )
  );

  // const routerWithO11y = withFaroRouterInstrumentation(router);

  // useEffect(() => {
  // initializeO11y();
  // }, []);
  //

  return (
    <Auth0Provider
      domain={CONFIG.AUTH0_DOMAIN}
      clientId={CONFIG.AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: CONFIG.AUTH0_AUD,
      }}
      skipRedirectCallback={window.location.pathname === "/oauth/aps/callback"}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
        <QueryClientProvider client={queryClient}>
          <CssBaseline />
          <Suspense
            fallback={
              <Skeleton
                height="100%"
                sx={{
                  transform: "none",
                  WebkitTransform: "none",
                }}
              />
            }
          >
            <RouterProvider router={router} />
          </Suspense>
          <ToastContainer 
            position="bottom-right" 
            theme={theme.palette.mode}
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            style={{ zIndex: 9999 }}
          />
        </QueryClientProvider>
      </LocalizationProvider>
    </Auth0Provider>
  );
}

export default App;
